export class Pager
{
    private $pageIndex: number = 0;
    private $pageSize: number = 10;
    private $sorting: string = '';
    private $order: string = 'ASC';
    private $totalRows: number = 0;
    private $loading: boolean = false;

    /**
     * Create a new Pager instance.
     */
    public constructor(pageIndex: number, pageSize: number = 20, sorting: string = 'Id', order: string = 'ASC')
    {
        this.setTotalRows(Number.MAX_SAFE_INTEGER);
        this.setPageSize(pageSize);
        this.setPageIndex(pageIndex);
        this.setSort(sorting, order);
    }

    public get pageIndex()
    {
        return this.getPageIndex();
    }

    public set pageIndex(value: number)
    {
        this.setPageIndex(value);
    }

    public get pageSize()
    {
        return this.getPageSize();
    }

    public set pageSize(value: number)
    {
        this.setPageSize(value);
    }

    public get sorting()
    {
        return this.getSort();
    }

    public set sorting(value: string)
    {
        this.setSort(value);
    }

    public get order()
    {
        return this.getOrder();
    }

    public set order(value: string)
    {
        this.setOrder(value);
    }

    public get totalRows()
    {
        return this.getTotalRows();
    }

    public set totalRows(value: number)
    {
        this.setTotalRows(value);
    }

    public get totalPages()
    {
        return this.getTotalPages();
    }

    public get offset()
    {
        return this.getOffset();
    }

    public get loading()
    {
        return this.$loading;
    }

    public set loading(value: boolean)
    {
        this.setLoading(value);
    }

    public setPageIndex(index: number)
    {
        this.$pageIndex = !isNaN(index) && index > 0 ? index : 1;
    }

    public getPageIndex()
    {
        if (this.$pageIndex < this.getTotalPages())
            return this.$pageIndex;
        else if (this.getTotalPages() > 0)
            return this.getTotalPages();
        else
            return 1;
    }

    public setPageSize(size: number)
    {
        this.$pageSize = !isNaN(size) && size > 0 ? size : 10;
    }

    public getPageSize()
    {
        return this.$pageSize;
    }

    public sort(sorting: string)
    {
        this.$sorting = sorting;
    }

    public setSort(sorting: string, order: string = 'ASC')
    {
        this.$sorting = sorting;
        this.setOrder(order);
    }

    public getSort()
    {
        return this.$sorting;
    }

    public setOrder(order: string)
    {
        this.$order = order.toUpperCase() === 'ASC' ? 'ASC' : 'DESC';
    }

    public getOrder()
    {
        return this.$order;
    }

    public setLoading(value: boolean)
    {
        this.$loading = typeof value === 'boolean' ? value : false;
    }

    public setTotalRows(rows: number)
    {
        this.$totalRows = !isNaN(rows) && rows > 0 ? rows : 0;
        this.setPageIndex(this.getPageIndex());
    }

    public getTotalRows()
    {
        return this.$totalRows;
    }

    public getTotalPages()
    {
        if (this.getPageSize() > 0)
            return Math.ceil(this.getTotalRows() / this.getPageSize());
        else
            return 0;
    }

    public getOffset()
    {
        return (this.getPageIndex() - 1) * this.getPageSize();
    }

    public data()
    {
        return {
            index: this.getPageIndex(),
            size: this.getPageSize(),
            sort: this.getSort(),
            order: this.getOrder()
        };
    }

    public shouldReloadList(requestedPageIndex: number, itemsCount: number, totalRows: number)
    {
        this.setTotalRows(totalRows);

        return itemsCount === 0 && this.getTotalRows() > 0 && requestedPageIndex !== this.getPageIndex();
    }

    public apply(data: PagerContract)
    {
        this.setPageIndex(data.index);
        this.setPageSize(data.size);
        this.setSort(data.sort, data.order);
    }
}

export interface PagerContract
{
    index: number;
    size: number;
    sort: string;
    order: string;
}

export const instanceOfPagerContract = (object: any): object is PagerContract =>
{
    return object && 'index' in object && 'size' in object && 'sort' in object && 'order' in object;
};
